/**
 * DecisionFlow — AcceleratorCapManager
 * data-fit LLC · Confidential
 * Domain: P3-03 Accelerator Caps (Standard tier — feature_gate_key = NULL)
 * Spec authority: decisionflow_phase3_plan_spec_v1_0.md §4
 * API Contract: api_contract_v1_32.md (cap fields on existing endpoints)
 * Wizard substages:
 *   cap_configuration · cap_validation
 *
 * What this manager does:
 *   - Lists plan_components for the active fiscal period
 *   - For a selected plan_component, reads / writes max_period_payout +
 *     max_period_payout_currency (period cap) and max_deal_commission
 *     (deal-level cap on the component's rate_table)
 *   - Reads cap-detail for a payout summary (post-run audit)
 *
 * DDL ground truth (phase3_plan_spec §4.2):
 *   plan_component.max_period_payout          numeric(18,4)  nullable
 *   plan_component.max_period_payout_currency char(3)        nullable
 *   rate_table.max_deal_commission            numeric(18,4)  nullable
 *   payout_summary.period_cap_applied         bool
 *   payout_summary.period_cap_amount          numeric(18,4)
 *   payout_summary.period_cap_clipped         numeric(18,4)
 *   payout_detail.deal_cap_applied            bool
 *   payout_detail.deal_cap_clipped            numeric(18,4)
 *
 * API surface (api_contract_v1_32.md):
 *   GET  /api/v1/plan-components?fiscal_period_id={id}
 *   GET  /api/v1/plan-components/{id}
 *   PUT  /api/v1/plan-components/{id}   (adds max_period_payout fields)
 *   GET  /api/v1/rate-tables?plan_component_id={id}
 *   PUT  /api/v1/rate-tables/{id}       (adds max_deal_commission field)
 *   GET  /api/v1/payout-summaries/{id}/cap-detail
 *
 * Architecture rules (matching SpiffPlanManager / TransactionPlanManager):
 *   - useAuth() for { user } — token never in props (ARCH-2)
 *   - apiGet / apiPost from client.js — PATCH via raw fetch (no apiPatch export)
 *   - tenant_id never in any request body (from JWT only)
 *   - No window.prompt — inline confirmation state throughout
 *   - All CSS via rgb(var(--color-*)) and text-tok-* tokens
 *   - GateStatusBanner for tier gate (not applicable here — Standard, but guard 403)
 *   - StatusPill imported from shared
 *   - No plan_status column — cap config has no approval workflow (columns on
 *     existing approved entities); validation substage is informational only
 *
 * Props:
 *   mode          — 'wizard' | 'admin' (wizard context shows substage header)
 *   period        — { fiscal_period_id, period_code } | null
 *   wizardSteps   — array of substage step objects (wizard context only)
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../auth/AuthContext.jsx';
import { apiGet, apiPost } from '../../api/client';
import GateStatusBanner from '../shared/GateStatusBanner.jsx';
import StatusPill from '../shared/StatusPill.jsx';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const CURRENCY_CODES = ['USD', 'EUR', 'GBP', 'CAD', 'AUD', 'SGD', 'JPY', 'CNY'];

const TABS = [
  { key: 'caps',       label: 'Cap Configuration' },
  { key: 'validation', label: 'Cap Validation'     },
];

const EMPTY_PERIOD_CAP_FORM = {
  max_period_payout:          '',
  max_period_payout_currency: 'USD',
};

const EMPTY_DEAL_CAP_FORM = {
  max_deal_commission: '',
};

// ---------------------------------------------------------------------------
// Shared CSS class strings — token-compliant (matches SpiffPlanManager)
// ---------------------------------------------------------------------------

const thClass =
  'px-3 py-2 text-left text-tok-xs font-semibold uppercase tracking-wide ' +
  'text-[rgb(var(--color-text-muted))] bg-[rgb(var(--color-surface-muted))] ' +
  'border-b border-[rgb(var(--color-border))]';

const tdClass =
  'px-3 py-2 text-tok-sm text-[rgb(var(--color-text))] ' +
  'border-b border-[rgb(var(--color-border))]';

const btnPrimary =
  'rounded-lg bg-[rgb(var(--color-primary))] px-3 py-1.5 text-tok-sm font-medium ' +
  'text-white hover:bg-[rgb(var(--color-primary-hover))] ' +
  'disabled:opacity-50 disabled:cursor-not-allowed';

const btnSecondary =
  'rounded-lg bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] ' +
  'px-3 py-1.5 text-tok-sm font-medium text-[rgb(var(--color-text))] ' +
  'hover:bg-[rgb(var(--color-surface-muted))] ' +
  'disabled:opacity-50 disabled:cursor-not-allowed';

const btnDanger =
  'rounded-lg border border-[rgb(var(--color-danger))]/40 px-3 py-1.5 text-tok-sm ' +
  'font-medium text-[rgb(var(--color-danger))] hover:bg-[rgb(var(--color-danger))]/10 ' +
  'disabled:opacity-50 disabled:cursor-not-allowed';

const inputClass =
  'mt-1 block w-full rounded-lg border border-[rgb(var(--color-border))] ' +
  'bg-[rgb(var(--color-surface))] text-[rgb(var(--color-text))] ' +
  'px-3 py-2 text-tok-sm focus:outline-none ' +
  'focus:ring-2 focus:ring-[rgb(var(--color-focus-ring))]';

const labelClass = 'flex flex-col text-tok-sm font-medium text-[rgb(var(--color-text))]';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getErrorMessage(error, fallback) {
  return error?.message || error?.detail || fallback;
}

function getApiErrorCode(error) {
  return error?.code || null;
}

function fmtMoney(val) {
  if (val === null || val === undefined || val === '') return '—';
  return Number(val).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtBool(val) {
  if (val === true)  return 'Yes';
  if (val === false) return 'No';
  return '—';
}

// ---------------------------------------------------------------------------
// Sub-component: CapDetail audit panel (post-run, read-only)
// ---------------------------------------------------------------------------

function CapDetailPanel({ payoutSummaryId, onClose }) {
  const [detail, setDetail]   = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  useEffect(() => {
    if (!payoutSummaryId) return;
    setLoading(true);
    setError('');
    apiGet(`/api/v1/payout-summaries/${payoutSummaryId}/cap-detail`)
      .then((data) => setDetail(data))
      .catch((err) => setError(getErrorMessage(err, 'Unable to load cap detail.')))
      .finally(() => setLoading(false));
  }, [payoutSummaryId]);

  return (
    <div className="rounded-xl bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] p-5 flex flex-col gap-4 shadow-sm">
      <div className="flex items-center justify-between">
        <h4 className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">Cap application detail</h4>
        <button type="button" className={btnSecondary} onClick={onClose}>Close</button>
      </div>

      {loading && <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">Loading cap detail…</p>}

      {error && (
        <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
          {error}
        </div>
      )}

      {detail && !loading && (
        <dl className="grid grid-cols-1 sm:grid-cols-3 gap-x-6 gap-y-3 text-tok-sm">
          <div>
            <dt className="text-[rgb(var(--color-text-muted))] font-medium">Period cap applied</dt>
            <dd className="text-[rgb(var(--color-text))] font-medium">{fmtBool(detail.period_cap_applied)}</dd>
          </div>
          <div>
            <dt className="text-[rgb(var(--color-text-muted))] font-medium">Cap ceiling</dt>
            <dd className="text-[rgb(var(--color-text))]">
              {detail.period_cap_amount != null ? fmtMoney(detail.period_cap_amount) : '—'}
            </dd>
          </div>
          <div>
            <dt className="text-[rgb(var(--color-text-muted))] font-medium">Amount clipped</dt>
            <dd className={`font-medium ${detail.period_cap_clipped > 0 ? 'text-[rgb(var(--color-warning))]' : 'text-[rgb(var(--color-text))]'}`}>
              {detail.period_cap_clipped != null ? fmtMoney(detail.period_cap_clipped) : '—'}
            </dd>
          </div>
          {Array.isArray(detail.deal_cap_rows) && detail.deal_cap_rows.length > 0 && (
            <div className="sm:col-span-3">
              <dt className="text-[rgb(var(--color-text-muted))] font-medium mb-2">Deal-level caps applied</dt>
              <dd>
                <div className="overflow-x-auto rounded-lg border border-[rgb(var(--color-border))]">
                  <table className="min-w-full text-tok-sm border-collapse">
                    <thead>
                      <tr>
                        <th className={thClass}>Payout detail ID</th>
                        <th className={thClass}>Clipped amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detail.deal_cap_rows.map((row, i) => (
                        <tr key={row.payout_detail_id || i} className="hover:bg-[rgb(var(--color-surface-muted))]">
                          <td className={tdClass}>{row.payout_detail_id}</td>
                          <td className={tdClass}>{fmtMoney(row.deal_cap_clipped)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </dd>
            </div>
          )}
        </dl>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function AcceleratorCapManager({ mode, period, wizardSteps, embedded = false, planId, planComponentId, fiscalPeriodId, readOnly = false }) {
  const { user } = useAuth();

  const [activeTab, setActiveTab]             = useState('caps');
  const [featureUnavailable, setFeatureUnavailable] = useState(false);
  const [toast, setToast]                     = useState(null);

  // Plan components list
  const [components, setComponents]           = useState([]);
  const [componentsLoading, setComponentsLoading] = useState(false);
  const [componentsError, setComponentsError] = useState('');

  // Selected component + detail
  const [selectedComponent, setSelectedComponent] = useState(null);
  const [componentDetail, setComponentDetail] = useState(null);
  const [detailLoading, setDetailLoading]     = useState(false);

  // Rate table for the selected component (deal cap)
  const [rateTable, setRateTable]             = useState(null);
  const [rateTableLoading, setRateTableLoading] = useState(false);

  // Period cap form
  const [periodCapForm, setPeriodCapForm]     = useState(EMPTY_PERIOD_CAP_FORM);
  const [periodCapSaving, setPeriodCapSaving] = useState(false);
  const [periodCapError, setPeriodCapError]   = useState('');
  const [periodCapEditing, setPeriodCapEditing] = useState(false);

  // Deal cap form
  const [dealCapForm, setDealCapForm]         = useState(EMPTY_DEAL_CAP_FORM);
  const [dealCapSaving, setDealCapSaving]     = useState(false);
  const [dealCapError, setDealCapError]       = useState('');
  const [dealCapEditing, setDealCapEditing]   = useState(false);

  // Inline confirmation state for destructive cap removal actions
  const [confirmPeriodCapRemove, setConfirmPeriodCapRemove] = useState(false);
  const [confirmDealCapRemove, setConfirmDealCapRemove]     = useState(false);

  // Cap detail panel (post-run audit)
  const [capDetailSummaryId, setCapDetailSummaryId] = useState('');
  const [showCapDetail, setShowCapDetail]     = useState(false);

  // Validation tab
  const [validationLoading, setValidationLoading] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [validationError, setValidationError] = useState('');

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  const showToast = useCallback((message) => {
    setToast(message);
    window.setTimeout(() => setToast(null), 3000);
  }, []);

  // ---------------------------------------------------------------------------
  // Load plan components
  // ---------------------------------------------------------------------------

  const loadComponents = useCallback(async () => {
    if (!user) return;
    setComponentsLoading(true);
    setComponentsError('');
    try {
      const periodId = period?.fiscal_period_id;
      // S685: scope to planId when provided so manager shows only this plan's components
      const params = new URLSearchParams();
      if (periodId) params.set('fiscal_period_id', periodId);
      if (planId)   params.set('plan_id', planId);
      const url = `/api/v1/plan-components${params.toString() ? `?${params.toString()}` : ''}`;
      const data = await apiGet(url);
      setComponents(Array.isArray(data) ? data : data?.data || data?.plan_components || []);
    } catch (error) {
      if (getApiErrorCode(error) === 'FEATURE_UNAVAILABLE' || error?.status === 403) {
        setFeatureUnavailable(true);
        return;
      }
      setComponentsError(getErrorMessage(error, 'Unable to load plan components.'));
    } finally {
      setComponentsLoading(false);
    }
  }, [user, period]);

  useEffect(() => { loadComponents(); }, [loadComponents]);

  // Embedded context injection (spec §4.8)
  useEffect(() => {
    if (embedded && planComponentId != null && components.length > 0) {
      const match = components.find((c) => String(c.plan_component_id) === String(planComponentId));
      if (match) setSelectedComponent(match);
    }
  }, [embedded, planComponentId, components]);

  // ---------------------------------------------------------------------------
  // Load component detail + rate table on selection
  // ---------------------------------------------------------------------------

  const loadComponentDetail = useCallback(async (componentId) => {
    setDetailLoading(true);
    try {
      const data = await apiGet(`/api/v1/plan-components/${componentId}`);
      setComponentDetail(data);
      setPeriodCapForm({
        max_period_payout:          data.max_period_payout ?? '',
        max_period_payout_currency: data.max_period_payout_currency ?? 'USD',
      });
    } catch (error) {
      setComponentsError(getErrorMessage(error, 'Unable to load component detail.'));
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const loadRateTable = useCallback(async (componentId) => {
    setRateTableLoading(true);
    setRateTable(null);
    try {
      const data = await apiGet(`/api/v1/rate-tables?plan_component_id=${componentId}`);
      // Take the first active rate table for this component
      const tables = Array.isArray(data) ? data : data?.data || data?.rate_tables || [];
      const active = tables.find((t) => t.is_active !== false) || tables[0] || null;
      setRateTable(active || null);
      if (active) {
        setDealCapForm({ max_deal_commission: active.max_deal_commission ?? '' });
      }
    } catch {
      // Non-blocking — component may not have a rate table yet
      setRateTable(null);
    } finally {
      setRateTableLoading(false);
    }
  }, []);

  const handleSelectComponent = useCallback((component) => {
    setSelectedComponent(component);
    setComponentDetail(null);
    setRateTable(null);
    setPeriodCapEditing(false);
    setDealCapEditing(false);
    setPeriodCapError('');
    setDealCapError('');
    setShowCapDetail(false);
    loadComponentDetail(component.plan_component_id);
    loadRateTable(component.plan_component_id);
  }, [loadComponentDetail, loadRateTable]);

  // ---------------------------------------------------------------------------
  // Period cap save (PATCH plan_component)
  // ---------------------------------------------------------------------------

  const validatePeriodCap = useCallback(() => {
    const val = periodCapForm.max_period_payout;
    if (val !== '' && val !== null) {
      if (isNaN(Number(val)) || Number(val) <= 0) return 'Period cap must be a positive number.';
      if (!periodCapForm.max_period_payout_currency) return 'Currency code is required when setting a period cap.';
    }
    return '';
  }, [periodCapForm]);

  const savePeriodCap = useCallback(async () => {
    const err = validatePeriodCap();
    if (err) { setPeriodCapError(err); return; }
    setPeriodCapSaving(true);
    setPeriodCapError('');
    try {
      const payload = {
        max_period_payout:          periodCapForm.max_period_payout !== '' ? periodCapForm.max_period_payout : null,
        max_period_payout_currency: periodCapForm.max_period_payout !== '' ? periodCapForm.max_period_payout_currency : null,
      };
      const res = await fetch(`/api/v1/plan-components/${selectedComponent.plan_component_id}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        const code = body?.error?.code || body?.code;
        if (code === 'CAP_CURRENCY_MISMATCH') throw new Error('Currency must match this position\'s payout currency.');
        if (code === 'CAP_NEGATIVE_VALUE')    throw new Error('Cap value must be greater than zero.');
        throw new Error(body?.error?.message || body?.detail || `Save failed (${res.status})`);
      }
      await loadComponentDetail(selectedComponent.plan_component_id);
      setPeriodCapEditing(false);
      showToast('Period cap saved.');
    } catch (error) {
      setPeriodCapError(error.message || 'Unable to save period cap.');
    } finally {
      setPeriodCapSaving(false);
    }
  }, [loadComponentDetail, periodCapForm, selectedComponent, showToast, validatePeriodCap]);

  const clearPeriodCap = useCallback(async () => {
    setPeriodCapSaving(true);
    setPeriodCapError('');
    try {
      const res = await fetch(`/api/v1/plan-components/${selectedComponent.plan_component_id}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ max_period_payout: null, max_period_payout_currency: null }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.error?.message || body?.detail || `Remove failed (${res.status})`);
      }
      await loadComponentDetail(selectedComponent.plan_component_id);
      setPeriodCapForm(EMPTY_PERIOD_CAP_FORM);
      setPeriodCapEditing(false);
      showToast('Period cap removed.');
    } catch (error) {
      setPeriodCapError(error.message || 'Unable to remove period cap.');
    } finally {
      setPeriodCapSaving(false);
    }
  }, [loadComponentDetail, selectedComponent, showToast]);

  // ---------------------------------------------------------------------------
  // Deal cap save (PATCH rate_table)
  // ---------------------------------------------------------------------------

  const validateDealCap = useCallback(() => {
    const val = dealCapForm.max_deal_commission;
    if (val !== '' && val !== null) {
      if (isNaN(Number(val)) || Number(val) <= 0) return 'Deal cap must be a positive number.';
    }
    return '';
  }, [dealCapForm]);

  const saveDealCap = useCallback(async () => {
    if (!rateTable) { setDealCapError('No rate table found for this plan component.'); return; }
    const err = validateDealCap();
    if (err) { setDealCapError(err); return; }
    setDealCapSaving(true);
    setDealCapError('');
    try {
      const payload = {
        max_deal_commission: dealCapForm.max_deal_commission !== '' ? dealCapForm.max_deal_commission : null,
      };
      const res = await fetch(`/api/v1/rate-tables/${rateTable.rate_table_id}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        const code = body?.error?.code || body?.code;
        if (code === 'CAP_NEGATIVE_VALUE') throw new Error('Deal cap must be greater than zero.');
        throw new Error(body?.error?.message || body?.detail || `Save failed (${res.status})`);
      }
      await loadRateTable(selectedComponent.plan_component_id);
      setDealCapEditing(false);
      showToast('Deal cap saved.');
    } catch (error) {
      setDealCapError(error.message || 'Unable to save deal cap.');
    } finally {
      setDealCapSaving(false);
    }
  }, [dealCapForm, loadRateTable, rateTable, selectedComponent, showToast, validateDealCap]);

  const clearDealCap = useCallback(async () => {
    if (!rateTable) return;
    setDealCapSaving(true);
    setDealCapError('');
    try {
      const res = await fetch(`/api/v1/rate-tables/${rateTable.rate_table_id}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ max_deal_commission: null }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.error?.message || body?.detail || `Remove failed (${res.status})`);
      }
      await loadRateTable(selectedComponent.plan_component_id);
      setDealCapForm(EMPTY_DEAL_CAP_FORM);
      setDealCapEditing(false);
      showToast('Deal cap removed.');
    } catch (error) {
      setDealCapError(error.message || 'Unable to remove deal cap.');
    } finally {
      setDealCapSaving(false);
    }
  }, [loadRateTable, rateTable, selectedComponent, showToast]);

  // ---------------------------------------------------------------------------
  // Validation tab — check cap health
  // ---------------------------------------------------------------------------

  const runValidation = useCallback(async () => {
    if (!period?.fiscal_period_id) {
      setValidationError('A fiscal period is required to run cap validation.');
      return;
    }
    setValidationLoading(true);
    setValidationError('');
    setValidationResult(null);
    try {
      // Cap validation reads plan_component list and checks cap vs. OTE
      const periodId = period.fiscal_period_id;
      const data = await apiGet(`/api/v1/plan-components?fiscal_period_id=${periodId}`);
      const list = Array.isArray(data) ? data : data?.data || data?.plan_components || [];
      const warnings = list
        .filter((c) => c.max_period_payout != null)
        .map((c) => {
          const cap = Number(c.max_period_payout);
          const ote = Number(c.variable_comp_amount || 0);
          const ratio = ote > 0 ? cap / ote : null;
          return {
            plan_component_id: c.plan_component_id,
            role_code:         c.role_code || c.role_id,
            cap_amount:        cap,
            currency:          c.max_period_payout_currency,
            ote_variable:      ote,
            ratio,
            warn: ratio != null && ratio < 1.0,
          };
        });
      setValidationResult({ components: list.length, capped: warnings.length, warnings });
    } catch (error) {
      setValidationError(getErrorMessage(error, 'Unable to run cap validation.'));
    } finally {
      setValidationLoading(false);
    }
  }, [period]);

  // ---------------------------------------------------------------------------
  // Feature gate
  // ---------------------------------------------------------------------------

  if (featureUnavailable) return <GateStatusBanner feature="Accelerator Caps" />;

  if (!user) {
    return (
      <section className="flex flex-col gap-6">
        <h2 className="text-tok-xl font-semibold text-[rgb(var(--color-text))]">Accelerator Caps</h2>
        <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
          Authentication required.
        </div>
      </section>
    );
  }

  // ---------------------------------------------------------------------------
  // Component detail view
  // ---------------------------------------------------------------------------

  if (selectedComponent) {
    const hasPeriodCap  = componentDetail?.max_period_payout != null && componentDetail?.max_period_payout !== '';
    const hasDealCap    = rateTable?.max_deal_commission != null && rateTable?.max_deal_commission !== '';

    return (
      <section className="flex flex-col gap-6">
        {toast && (
          <div role="status" className="fixed bottom-4 right-4 z-50 rounded-lg bg-[rgb(var(--color-primary))] px-4 py-3 text-tok-sm text-white shadow-lg">
            {toast}
          </div>
        )}

        <header className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <button
              type="button"
              className={btnSecondary}
              onClick={() => {
                setSelectedComponent(null);
                setComponentDetail(null);
                setRateTable(null);
                setPeriodCapEditing(false);
                setDealCapEditing(false);
                setShowCapDetail(false);
              }}
            >
              ← Back to plan components
            </button>
            <h2 className="mt-3 text-tok-xl font-semibold text-[rgb(var(--color-text))]">
              {selectedComponent.role_code || selectedComponent.plan_component_id}
            </h2>
            <p className="mt-1 text-tok-sm text-[rgb(var(--color-text-muted))]">
              Plan component {selectedComponent.plan_component_id}
              {selectedComponent.segment_code ? ` · ${selectedComponent.segment_code}` : ''}
              {period?.period_code ? ` · ${period.period_code}` : ''}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {hasPeriodCap && <StatusPill status="capped" />}
            {!hasPeriodCap && !hasDealCap && <StatusPill status="uncapped" />}
          </div>
        </header>

        {detailLoading && (
          <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">Loading component detail…</p>
        )}

        {/* ── Period payout cap ── */}
        {!detailLoading && componentDetail && (
          <div className="rounded-xl bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] p-5 flex flex-col gap-4 shadow-sm">
            <div className="flex items-start justify-between gap-2">
              <div>
                <h3 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">Period payout cap</h3>
                <p className="mt-1 text-tok-sm text-[rgb(var(--color-text-muted))]">
                  Maximum total commission this rep can earn in a single fiscal period, regardless of attainment.
                  Cap applies after kicker multipliers. NULL = uncapped.
                </p>
              </div>
              {!periodCapEditing && (
                <button
                  type="button"
                  className={btnSecondary}
                  onClick={() => { setPeriodCapEditing(true); setPeriodCapError(''); }}
                >
                  {hasPeriodCap ? 'Edit cap' : 'Set cap'}
                </button>
              )}
            </div>

            {!periodCapEditing && (
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-tok-sm">
                <div>
                  <dt className="text-[rgb(var(--color-text-muted))] font-medium">Period cap</dt>
                  <dd className="text-[rgb(var(--color-text))] font-medium">
                    {hasPeriodCap
                      ? `${fmtMoney(componentDetail.max_period_payout)} ${componentDetail.max_period_payout_currency}`
                      : <span className="italic text-[rgb(var(--color-text-muted))]">No cap set</span>}
                  </dd>
                </div>
                <div>
                  <dt className="text-[rgb(var(--color-text-muted))] font-medium">Currency</dt>
                  <dd className="text-[rgb(var(--color-text))]">
                    {componentDetail.max_period_payout_currency || '—'}
                  </dd>
                </div>
              </dl>
            )}

            {periodCapEditing && (
              <div className="flex flex-col gap-3">
                {periodCapError && (
                  <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
                    {periodCapError}
                  </div>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <label className={labelClass}>
                    Maximum period payout
                    <input
                      type="number"
                      min="0.01"
                      step="0.01"
                      className={inputClass}
                      value={periodCapForm.max_period_payout}
                      onChange={(e) => setPeriodCapForm((prev) => ({ ...prev, max_period_payout: e.target.value }))}
                      placeholder="e.g. 250000.00 — leave blank to remove cap"
                    />
                  </label>
                  <label className={labelClass}>
                    Currency
                    <select
                      className={inputClass}
                      value={periodCapForm.max_period_payout_currency}
                      onChange={(e) => setPeriodCapForm((prev) => ({ ...prev, max_period_payout_currency: e.target.value }))}
                    >
                      {CURRENCY_CODES.map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  </label>
                </div>
                <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">
                  Currency must match this position's payout currency. Leave the cap field blank to remove the cap entirely.
                </p>
                <div className="flex items-center gap-2 flex-wrap">
                  <button
                    type="button"
                    className={btnPrimary}
                    onClick={savePeriodCap}
                    disabled={periodCapSaving}
                  >
                    {periodCapSaving ? 'Saving…' : 'Save cap'}
                  </button>
                  {hasPeriodCap && !confirmPeriodCapRemove && (
                    <button
                      type="button"
                      className={btnDanger}
                      onClick={() => setConfirmPeriodCapRemove(true)}
                      disabled={periodCapSaving}
                    >
                      Remove cap
                    </button>
                  )}
                  {hasPeriodCap && confirmPeriodCapRemove && (
                    <span className="flex items-center gap-2 text-tok-sm">
                      <span className="text-[rgb(var(--color-text-muted))]">Remove cap? This cannot be undone.</span>
                      <button type="button" className={btnDanger} onClick={() => { setConfirmPeriodCapRemove(false); clearPeriodCap(); }} disabled={periodCapSaving}>
                        Confirm remove
                      </button>
                      <button type="button" className={btnSecondary} onClick={() => setConfirmPeriodCapRemove(false)} disabled={periodCapSaving}>
                        Cancel
                      </button>
                    </span>
                  )}
                  <button
                    type="button"
                    className={btnSecondary}
                    onClick={() => {
                      setPeriodCapEditing(false);
                      setPeriodCapError('');
                      setPeriodCapForm({
                        max_period_payout:          componentDetail.max_period_payout ?? '',
                        max_period_payout_currency: componentDetail.max_period_payout_currency ?? 'USD',
                      });
                    }}
                    disabled={periodCapSaving}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Deal-level commission cap ── */}
        <div className="rounded-xl bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] p-5 flex flex-col gap-4 shadow-sm">
          <div className="flex items-start justify-between gap-2">
            <div>
              <h3 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">Deal-level commission cap</h3>
              <p className="mt-1 text-tok-sm text-[rgb(var(--color-text-muted))]">
                Maximum commission payable on any single credit record. Applied inline during rate table lookup,
                before the period cap. NULL = no deal-level cap.
              </p>
            </div>
            {!dealCapEditing && rateTable && (
              <button
                type="button"
                className={btnSecondary}
                onClick={() => { setDealCapEditing(true); setDealCapError(''); }}
              >
                {hasDealCap ? 'Edit cap' : 'Set cap'}
              </button>
            )}
          </div>

          {rateTableLoading && (
            <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">Loading rate table…</p>
          )}

          {!rateTableLoading && !rateTable && (
            <div className="rounded-lg bg-[rgb(var(--color-surface-muted))] border border-[rgb(var(--color-border))] px-4 py-3 text-tok-sm text-[rgb(var(--color-text-muted))]">
              No approved rate table found for this plan component. Configure a rate table in the Plan Builder before setting a deal cap.
            </div>
          )}

          {!rateTableLoading && rateTable && !dealCapEditing && (
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-tok-sm">
              <div>
                <dt className="text-[rgb(var(--color-text-muted))] font-medium">Deal commission cap</dt>
                <dd className="text-[rgb(var(--color-text))] font-medium">
                  {hasDealCap
                    ? fmtMoney(rateTable.max_deal_commission)
                    : <span className="italic text-[rgb(var(--color-text-muted))]">No cap set</span>}
                </dd>
              </div>
              <div>
                <dt className="text-[rgb(var(--color-text-muted))] font-medium">Rate table</dt>
                <dd className="text-[rgb(var(--color-text))]">
                  {rateTable.rate_table_code || rateTable.rate_table_id || '—'}
                </dd>
              </div>
            </dl>
          )}

          {!rateTableLoading && rateTable && dealCapEditing && (
            <div className="flex flex-col gap-3">
              {dealCapError && (
                <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
                  {dealCapError}
                </div>
              )}
              <label className={labelClass} style={{ maxWidth: '320px' }}>
                Maximum commission per deal
                <input
                  type="number"
                  min="0.01"
                  step="0.01"
                  className={inputClass}
                  value={dealCapForm.max_deal_commission}
                  onChange={(e) => setDealCapForm({ max_deal_commission: e.target.value })}
                  placeholder="e.g. 50000.00 — leave blank to remove cap"
                />
              </label>
              <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">
                Cap is applied in the rate table currency. Leave blank to remove the deal-level cap.
              </p>
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  type="button"
                  className={btnPrimary}
                  onClick={saveDealCap}
                  disabled={dealCapSaving}
                >
                  {dealCapSaving ? 'Saving…' : 'Save cap'}
                </button>
                {hasDealCap && !confirmDealCapRemove && (
                  <button
                    type="button"
                    className={btnDanger}
                    onClick={() => setConfirmDealCapRemove(true)}
                    disabled={dealCapSaving}
                  >
                    Remove cap
                  </button>
                )}
                {hasDealCap && confirmDealCapRemove && (
                  <span className="flex items-center gap-2 text-tok-sm flex-wrap">
                    <span className="text-[rgb(var(--color-text-muted))]">Remove deal cap?</span>
                    <button type="button" className={btnDanger} onClick={() => { setConfirmDealCapRemove(false); clearDealCap(); }} disabled={dealCapSaving}>
                      Confirm remove
                    </button>
                    <button type="button" className={btnSecondary} onClick={() => setConfirmDealCapRemove(false)} disabled={dealCapSaving}>
                      Cancel
                    </button>
                  </span>
                )}
                <button
                  type="button"
                  className={btnSecondary}
                  onClick={() => {
                    setDealCapEditing(false);
                    setDealCapError('');
                    setDealCapForm({ max_deal_commission: rateTable?.max_deal_commission ?? '' });
                  }}
                  disabled={dealCapSaving}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>

        {/* ── Post-run cap detail audit ── */}
        <div className="rounded-xl bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] p-5 flex flex-col gap-4 shadow-sm">
          <h3 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">Post-run cap audit</h3>
          <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">
            After a calculation run, enter a payout summary ID to review whether and how caps were applied.
          </p>
          <div className="flex items-end gap-3 flex-wrap">
            <label className={labelClass} style={{ flex: '1', minWidth: '240px' }}>
              Payout summary ID
              <input
                type="text"
                className={inputClass}
                value={capDetailSummaryId}
                onChange={(e) => setCapDetailSummaryId(e.target.value)}
                placeholder="UUID from calculation run results"
              />
            </label>
            <button
              type="button"
              className={btnSecondary}
              disabled={!capDetailSummaryId.trim()}
              onClick={() => setShowCapDetail(true)}
            >
              View cap detail
            </button>
          </div>
          {showCapDetail && capDetailSummaryId && (
            <CapDetailPanel
              payoutSummaryId={capDetailSummaryId}
              onClose={() => { setShowCapDetail(false); setCapDetailSummaryId(''); }}
            />
          )}
        </div>
      </section>
    );
  }

  // ---------------------------------------------------------------------------
  // Main tabbed view
  // ---------------------------------------------------------------------------

  return (
    <section className="flex flex-col gap-6">
      {toast && (
        <div role="status" className="fixed bottom-4 right-4 z-50 rounded-lg bg-[rgb(var(--color-primary))] px-4 py-3 text-tok-sm text-white shadow-lg">
          {toast}
        </div>
      )}

      <header>
        {mode === 'wizard' && (
          <p className="text-tok-xs font-semibold uppercase tracking-widest text-[rgb(var(--color-text-muted))] mb-1">
            Stage 8 · P3-03
          </p>
        )}
        <h2 className="text-tok-xl font-semibold text-[rgb(var(--color-text))]">Accelerator Caps</h2>
        <p className="mt-1 text-tok-sm text-[rgb(var(--color-text-muted))]">
          Configure period payout ceilings and deal-level commission caps per plan component.
          Caps are applied after kicker multipliers and are enforced by the engine on every run.
        </p>
      </header>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-[rgb(var(--color-border))]">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => setActiveTab(tab.key)}
            className={[
              'px-4 py-2 text-tok-sm font-medium border-b-2 -mb-px transition-colors',
              activeTab === tab.key
                ? 'border-[rgb(var(--color-primary))] text-[rgb(var(--color-primary))]'
                : 'border-transparent text-[rgb(var(--color-text-muted))] hover:text-[rgb(var(--color-text))]',
            ].join(' ')}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── Cap Configuration tab ── */}
      {activeTab === 'caps' && (
        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-between gap-2">
            <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">
              Select a plan component to view or edit its cap configuration.
              {period?.period_code ? ` Showing components for ${period.period_code}.` : ''}
            </p>
          </div>

          {componentsLoading && (
            <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">Loading plan components…</p>
          )}

          {componentsError && (
            <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
              {componentsError}
            </div>
          )}

          {!componentsLoading && !componentsError && components.length === 0 && (
            <div className="rounded-lg bg-[rgb(var(--color-surface-muted))] border border-[rgb(var(--color-border))] px-5 py-8 text-center">
              <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">
                No plan components found{period?.period_code ? ` for ${period.period_code}` : ''}.
                Plan components are created in the Plan Builder (Stage 8, Data Setup).
              </p>
            </div>
          )}

          {components.length > 0 && (
            <div className="rounded-xl bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] shadow-sm overflow-x-auto">
              <table className="min-w-full text-tok-sm border-collapse">
                <thead>
                  <tr>
                    <th className={thClass}>Role</th>
                    <th className={thClass}>Segment</th>
                    <th className={thClass}>Period cap</th>
                    <th className={thClass}>Currency</th>
                    <th className={thClass} aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  {components.map((c) => {
                    const hasCap = c.max_period_payout != null && c.max_period_payout !== '';
                    return (
                      <tr key={c.plan_component_id} className="hover:bg-[rgb(var(--color-surface-muted))]">
                        <td className={tdClass + ' font-medium'}>{c.role_code || c.role_id || '—'}</td>
                        <td className={tdClass}>{c.segment_code || c.segment_id || '—'}</td>
                        <td className={tdClass}>
                          {hasCap
                            ? <span className="font-medium text-[rgb(var(--color-text))]">{fmtMoney(c.max_period_payout)}</span>
                            : <span className="italic text-[rgb(var(--color-text-muted))]">No cap</span>}
                        </td>
                        <td className={tdClass}>{c.max_period_payout_currency || '—'}</td>
                        <td className={tdClass}>
                          <button
                            type="button"
                            className="text-tok-sm text-[rgb(var(--color-primary))] hover:underline"
                            onClick={() => handleSelectComponent(c)}
                          >
                            Configure
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── Cap Validation tab ── */}
      {activeTab === 'validation' && (
        <div className="flex flex-col gap-4">
          <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">
            Checks that all configured caps are positive, currency-consistent, and not set below the
            expected variable OTE — which would clip all or most payouts unintentionally.
            This is an advisory check only; no hard block is enforced here.
          </p>

          <div>
            <button
              type="button"
              className={btnPrimary}
              onClick={runValidation}
              disabled={validationLoading}
            >
              {validationLoading ? 'Running…' : 'Run cap validation'}
            </button>
          </div>

          {validationError && (
            <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
              {validationError}
            </div>
          )}

          {validationResult && (
            <div className="flex flex-col gap-4">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <div className="rounded-lg bg-[rgb(var(--color-surface-muted))] border border-[rgb(var(--color-border))] px-4 py-3">
                  <p className="text-tok-xs text-[rgb(var(--color-text-muted))] font-medium uppercase tracking-wide">Components</p>
                  <p className="mt-1 text-tok-lg font-semibold text-[rgb(var(--color-text))]">{validationResult.components}</p>
                </div>
                <div className="rounded-lg bg-[rgb(var(--color-surface-muted))] border border-[rgb(var(--color-border))] px-4 py-3">
                  <p className="text-tok-xs text-[rgb(var(--color-text-muted))] font-medium uppercase tracking-wide">Capped</p>
                  <p className="mt-1 text-tok-lg font-semibold text-[rgb(var(--color-text))]">{validationResult.capped}</p>
                </div>
                <div className={`rounded-lg border px-4 py-3 ${validationResult.warnings.filter((w) => w.warn).length > 0 ? 'bg-[rgb(var(--color-warning))]/10 border-[rgb(var(--color-warning))]/30' : 'bg-[rgb(var(--color-surface-muted))] border-[rgb(var(--color-border))]'}`}>
                  <p className="text-tok-xs text-[rgb(var(--color-text-muted))] font-medium uppercase tracking-wide">Warnings</p>
                  <p className={`mt-1 text-tok-lg font-semibold ${validationResult.warnings.filter((w) => w.warn).length > 0 ? 'text-[rgb(var(--color-warning))]' : 'text-[rgb(var(--color-text))]'}`}>
                    {validationResult.warnings.filter((w) => w.warn).length}
                  </p>
                </div>
              </div>

              {validationResult.warnings.length > 0 && (
                <div className="rounded-xl bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] shadow-sm overflow-x-auto">
                  <table className="min-w-full text-tok-sm border-collapse">
                    <thead>
                      <tr>
                        <th className={thClass}>Role</th>
                        <th className={thClass}>Cap</th>
                        <th className={thClass}>Currency</th>
                        <th className={thClass}>OTE variable</th>
                        <th className={thClass}>Cap / OTE</th>
                        <th className={thClass}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {validationResult.warnings.map((w, i) => (
                        <tr key={w.plan_component_id || i} className="hover:bg-[rgb(var(--color-surface-muted))]">
                          <td className={tdClass + ' font-medium'}>{w.role_code || w.plan_component_id}</td>
                          <td className={tdClass}>{fmtMoney(w.cap_amount)}</td>
                          <td className={tdClass}>{w.currency || '—'}</td>
                          <td className={tdClass}>{w.ote_variable ? fmtMoney(w.ote_variable) : '—'}</td>
                          <td className={tdClass}>
                            {w.ratio != null ? `${(w.ratio * 100).toFixed(0)}%` : '—'}
                          </td>
                          <td className={tdClass}>
                            {w.warn
                              ? <span className="inline-block px-2 py-0.5 rounded text-tok-xs font-medium bg-[rgb(var(--color-warning))]/10 text-[rgb(var(--color-warning))]">Cap below OTE variable</span>
                              : <span className="inline-block px-2 py-0.5 rounded text-tok-xs font-medium bg-[rgb(var(--color-success))]/10 text-[rgb(var(--color-success))]">OK</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {validationResult.warnings.length === 0 && validationResult.capped === 0 && (
                <p className="text-tok-sm text-[rgb(var(--color-text-muted))] italic">
                  No caps configured for this period.
                </p>
              )}

              {validationResult.warnings.filter((w) => w.warn).length === 0 && validationResult.capped > 0 && (
                <div className="rounded-lg bg-[rgb(var(--color-success))]/10 border border-[rgb(var(--color-success))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-success))]">
                  All configured caps are above the expected variable OTE. No warnings.
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </section>
  );
}
